import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { hubApi } from "../../services/hubApi";
import { META_GRAPH_VERSION_PADRAO } from "../../config/metaGraph";

const channelLabels = {
  whatsapp: "WhatsApp Oficial",
  facebook: "Facebook Messenger",
  instagram: "Instagram Direct",
};

const steps = ["Autorizar", "Conectando", "Pronto"];

export default function MetaSignupFlow({ channel, onBack, onSuccess }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [metaConfig, setMetaConfig] = useState(null);

  useEffect(() => {
    hubApi.getMetaConfig().then(({ data }) => setMetaConfig(data));
  }, []);

  const loadFacebookSDK = useCallback(() => {
    return new Promise((resolve) => {
      if (window.FB) {
        resolve();
        return;
      }

      window.fbAsyncInit = function () {
        window.FB.init({
          appId: metaConfig?.appId,
          cookie: true,
          xfbml: true,
          version: metaConfig?.apiVersion || META_GRAPH_VERSION_PADRAO,
        });
        resolve();
      };

      if (!document.getElementById("facebook-jssdk")) {
        const script = document.createElement("script");
        script.id = "facebook-jssdk";
        script.src = "https://connect.facebook.net/pt_BR/sdk.js";
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    });
  }, [metaConfig]);

  const handleStartSignup = async () => {
    if (!metaConfig?.appId) {
      setError("Meta App ID nao configurado. Contate o administrador.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await loadFacebookSDK();

      const scopes =
        channel === "whatsapp"
          ? "whatsapp_business_management,whatsapp_business_messaging"
          : "pages_show_list,pages_messaging,pages_manage_metadata,pages_read_engagement";

      const extras =
        channel === "whatsapp" && metaConfig.configId
          ? {
              config_id: metaConfig.configId,
              override_default_response_type: true,
              response_type: "code",
            }
          : {};

      window.FB.login(
        (response) => {
          if (response.authResponse?.code) {
            completeSignup(response.authResponse.code);
          } else {
            setLoading(false);
            setError("Autorizacao cancelada pelo usuario.");
          }
        },
        {
          scope: scopes,
          ...extras,
        }
      );
    } catch (err) {
      setLoading(false);
      setError("Erro ao carregar Facebook SDK.");
    }
  };

  const completeSignup = async (code) => {
    setActiveStep(1);
    try {
      const { data } = await hubApi.metaEmbeddedSignup(code, channel);
      setResult(data);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao completar signup.");
      setActiveStep(0);
    } finally {
      setLoading(false);
    }
  };

  if (activeStep === 2 && result) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Conectado com sucesso!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {channelLabels[channel]}
        </Typography>
        {result.phoneNumber && (
          <Typography variant="body2" color="text.secondary">
            Numero: {result.phoneNumber}
          </Typography>
        )}
        {result.pageName && (
          <Typography variant="body2" color="text.secondary">
            Pagina: {result.pageName}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={onSuccess}
          sx={{
            mt: 3,
            background: "linear-gradient(135deg, #6B48FF 0%, #8B6FFF 100%)",
          }}
        >
          Concluir
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 3, color: "text.secondary" }}
      >
        Voltar
      </Button>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="h6" gutterBottom>
          Conectar {channelLabels[channel]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: "auto" }}>
          {channel === "whatsapp"
            ? "Clique no botao abaixo para autorizar sua conta do WhatsApp Business via Meta. Voce sera redirecionado para o fluxo de configuracao do Meta."
            : `Clique no botao abaixo para conectar sua Pagina do ${channel === "facebook" ? "Facebook" : "Instagram"} via Meta.`}
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={handleStartSignup}
          disabled={loading}
          sx={{
            py: 1.5,
            px: 5,
            fontSize: "1rem",
            background:
              channel === "whatsapp"
                ? "linear-gradient(135deg, #25D366 0%, #128C7E 100%)"
                : channel === "facebook"
                ? "linear-gradient(135deg, #1877F2 0%, #0D5BBF 100%)"
                : "linear-gradient(135deg, #E4405F 0%, #C13584 100%)",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            `Conectar ${channelLabels[channel]}`
          )}
        </Button>
      </Box>
    </Box>
  );
}
